/**
 * CertificateA4 — the ONE canonical CanniComply certificate.
 *
 * • Locked A4 landscape aspect ratio (297 × 210), rendered identically on:
 *     – landing hero (small preview card),
 *     – training / my-training modals,
 *     – downloaded PDF (via exportCertificatePDF, offscreen at 1122×793),
 *     – system print dialog,
 *     – public /verify/{id} page (embedded above the audit trail).
 * • All sizing in container-query units (cqi) so it scales continuously
 *   from a 320px mobile card to full-desktop without distortion.
 * • FitText auto-shrinks long names and competency titles so nothing
 *   ever overflows or overlaps regardless of the data.
 * • Only uses Google Fonts already loaded in index.html (Inter, Playfair
 *   Display italic/roman, JetBrains Mono) — no runtime font loading race
 *   with html2canvas during PDF export.
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import CannicomplyLogo from "../site/CannicomplyLogo";

const PALETTE = {
  paper: "#FBF9F3",
  paperEdge: "#EDE7D6",
  ink: "#0B1220",
  inkSoft: "#2B354A",
  inkMuted: "#5F6B84",
  hair: "#DFD7BE",
  rule: "#E9E0C6",
  gold: "#B98D46",
  goldSoft: "#D4A65A",
  goldRing: "#D4A65A",
  emerald: "#0F5D3F",
  watermark: "rgba(11,18,32,0.03)",
};

/* ============================================================
   FitText — auto-shrink text that always fits its parent.
   The single guard against long names or competency titles
   ever overlapping or breaking the layout.
   ============================================================ */
function FitText({
  children,
  maxSize,
  minSize,
  maxLines = 1,
  weight = 600,
  style = {},
  as: Tag = "div",
}) {
  const ref = useRef(null);
  const [size, setSize] = useState(maxSize);
  const [fontsReady, setFontsReady] = useState(false);

  useEffect(() => {
    if (typeof document === "undefined" || !document.fonts || !document.fonts.ready) {
      setFontsReady(true);
      return undefined;
    }
    let cancelled = false;
    document.fonts.ready.then(() => {
      if (!cancelled) setFontsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const parent = el.parentElement;
    if (!parent) return;
    const LH = 1.2;

    const fits = (s) => {
      el.style.fontSize = `${s}px`;
      el.style.lineHeight = `${LH}`;
      const overflowW = el.scrollWidth > parent.clientWidth - 2;
      const linesUsed = Math.ceil(el.scrollHeight / (s * LH));
      return !overflowW && linesUsed <= maxLines;
    };

    let low = minSize;
    let high = maxSize;
    let best = minSize;
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      if (fits(mid)) {
        best = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }
    el.style.fontSize = `${best}px`;
    el.style.lineHeight = `${LH}`;
    setSize(best);
  }, [children, maxSize, minSize, maxLines, fontsReady]);

  return (
    <Tag
      ref={ref}
      style={{
        ...style,
        fontSize: size,
        fontWeight: weight,
        lineHeight: 1.2,
        wordBreak: "break-word",
        display: "block",
      }}
    >
      {children}
    </Tag>
  );
}

/* ============================================================
   Icons — inline SVG so they render pixel-identical everywhere
   (browser preview, html2canvas PDF export, print).
   ============================================================ */
function IconBarChart() {
  return (
    <svg width="60%" height="60%" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M4 20 V13" stroke={PALETTE.emerald} strokeWidth="2.2" strokeLinecap="round" />
      <path d="M4 20 V13" stroke={PALETTE.emerald} strokeWidth="2.2" strokeLinecap="round" />
      <path d="M10 20 V9"  stroke={PALETTE.gold} strokeWidth="2.2" strokeLinecap="round" />
      <path d="M16 20 V5"  stroke={PALETTE.gold} strokeWidth="2.2" strokeLinecap="round" />
      <path d="M22 20 V15" stroke={PALETTE.gold} strokeWidth="2.2" strokeLinecap="round" />
      <path d="M3 21 H23"  stroke={PALETTE.goldSoft} strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}
function IconRibbon() {
  return (
    <svg width="58%" height="58%" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="9" r="5.4" stroke={PALETTE.gold} strokeWidth="1.6" fill="none" />
      <path d="M12 5.5 L13 8 L15.6 8.3 L13.6 10 L14.2 12.6 L12 11.3 L9.8 12.6 L10.4 10 L8.4 8.3 L11 8 Z" fill={PALETTE.goldSoft} />
      <path d="M8.5 13.2 L6.5 21 L9.5 19 L11.5 21 L12 15" stroke={PALETTE.gold} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" fill="none" />
      <path d="M15.5 13.2 L17.5 21 L14.5 19 L12.5 21 L12 15" stroke={PALETTE.gold} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" fill="none" />
    </svg>
  );
}
function IconCalendar() {
  return (
    <svg width="58%" height="58%" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3.5" y="5" width="17" height="15.5" rx="1.6" stroke={PALETTE.gold} strokeWidth="1.6" fill="none" />
      <path d="M3.5 9.5 H20.5" stroke={PALETTE.gold} strokeWidth="1.6" />
      <path d="M8 3.5 V6.5" stroke={PALETTE.gold} strokeWidth="1.8" strokeLinecap="round" />
      <path d="M16 3.5 V6.5" stroke={PALETTE.gold} strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="8" cy="14" r="1.1" fill={PALETTE.goldSoft} />
      <circle cx="12" cy="14" r="1.1" fill={PALETTE.goldSoft} />
      <circle cx="16" cy="14" r="1.1" fill={PALETTE.goldSoft} />
    </svg>
  );
}
function IconClock() {
  return (
    <svg width="58%" height="58%" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="8.5" stroke={PALETTE.gold} strokeWidth="1.6" fill="none" />
      <path d="M12 7.5 V12 L15.5 14" stroke={PALETTE.gold} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="12" cy="12" r="0.9" fill={PALETTE.gold} />
    </svg>
  );
}
function IconShield() {
  return (
    <svg width="55%" height="55%" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M12 2 L20 5 V12 C20 17 16.5 20.5 12 22 C7.5 20.5 4 17 4 12 V5 Z"
        stroke={PALETTE.emerald} strokeWidth="1.6" strokeLinejoin="round" fill="none" />
      <path d="M8.5 12 L11 14.5 L15.8 9.5"
        stroke={PALETTE.emerald} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}

/* ============================================================
   MetricCell — icon in a champagne-gold ring + label + value.
   Used in the four-column metadata band.
   ============================================================ */
function MetricCell({ Icon, label, value }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "1.2cqi",
        minWidth: 0,
      }}
    >
      <div
        aria-hidden
        style={{
          width: "3.4cqi",
          height: "3.4cqi",
          minWidth: 26,
          minHeight: 26,
          borderRadius: "50%",
          border: `1.2px solid ${PALETTE.goldRing}`,
          background: "rgba(212,166,90,0.08)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: "0 0 auto",
        }}
      >
        <Icon />
      </div>
      <div style={{ minWidth: 0, lineHeight: 1.2 }}>
        <div
          style={{
            textTransform: "uppercase",
            letterSpacing: "0.2em",
            fontSize: "0.82cqi",
            color: PALETTE.inkMuted,
            fontWeight: 600,
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: "1.35cqi",
            fontWeight: 700,
            color: PALETTE.ink,
            marginTop: "0.15em",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Fleuron — centred ornamental divider between body sections.
   Two hairline segments meet a small gold diamond in the middle.
   ============================================================ */
function Fleuron() {
  return (
    <div
      aria-hidden
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "1.4cqi",
      }}
    >
      <span
        style={{
          flex: 1,
          height: 1,
          background: `linear-gradient(to right, transparent, ${PALETTE.goldSoft}88 60%, ${PALETTE.gold})`,
        }}
      />
      <span
        style={{
          width: 6,
          height: 6,
          background: PALETTE.gold,
          transform: "rotate(45deg)",
          flex: "0 0 auto",
          boxShadow: `0 0 0 3px ${PALETTE.paper}, 0 0 0 4px ${PALETTE.goldSoft}55`,
        }}
      />
      <span
        style={{
          flex: 1,
          height: 1,
          background: `linear-gradient(to left, transparent, ${PALETTE.goldSoft}88 60%, ${PALETTE.gold})`,
        }}
      />
    </div>
  );
}

/* ============================================================
   VerifiedBadge — small tick + label. Deliberately low-key so
   it never competes with the logo for visual weight in the
   top-corner row of the header.
   ============================================================ */
function VerifiedBadge() {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.5em",
        color: PALETTE.inkMuted,
      }}
    >
      <svg width={14} height={14} viewBox="0 0 24 24" aria-hidden style={{ flex: "0 0 auto" }}>
        <circle cx="12" cy="12" r="10" fill={PALETTE.emerald} />
        <path
          d="M7.5 12.2 L10.8 15.5 L16.5 9"
          stroke="#FFFFFF"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
      <span
        style={{
          textTransform: "uppercase",
          letterSpacing: "0.28em",
          fontSize: "0.78cqi",
          fontWeight: 600,
          color: PALETTE.inkMuted,
          lineHeight: 1,
        }}
      >
        Verified certificate
      </span>
    </div>
  );
}

/* ============================================================
   QrBlock — real scannable QR encoded from the verify URL.
   ============================================================ */
function QrBlock({ url, size }) {
  let matrix = null;
  try {
    if (url) {
      const qr = QRCode.create(url, { errorCorrectionLevel: "M" });
      matrix = { data: qr.modules.data, dim: qr.modules.size };
    }
  } catch (err) {
    matrix = null;
  }
  return (
    <div
      aria-label={url ? `Certificate verification QR · ${url}` : "Certificate verification QR"}
      style={{
        width: size,
        height: size,
        padding: 4,
        background: "#FFFFFF",
        border: `1px solid ${PALETTE.ink}`,
        boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
        flex: "0 0 auto",
      }}
    >
      {matrix && (
        <svg viewBox={`0 0 ${matrix.dim} ${matrix.dim}`} width="100%" height="100%" shapeRendering="crispEdges">
          {Array.from(matrix.data).map((bit, i) => {
            if (!bit) return null;
            const x = i % matrix.dim;
            const y = Math.floor(i / matrix.dim);
            return <rect key={i} x={x} y={y} width={1} height={1} fill={PALETTE.ink} />;
          })}
        </svg>
      )}
    </div>
  );
}

/* ============================================================
   CornerMarker — gold ornament in each of the four corners of
   the certificate frame.
   ============================================================ */
function CornerMarker({ position }) {
  // Insets and size in `cqi` (1% of the card's inline size) so both axes
  // resolve against the same dimension. Using `%` measured vertical vs
  // width, so the ornament never squared up with the border frame.
  const map = {
    tl: { top: "0.6cqi", left: "0.6cqi", transform: "rotate(0deg)" },
    tr: { top: "0.6cqi", right: "0.6cqi", transform: "rotate(90deg)" },
    bl: { bottom: "0.6cqi", left: "0.6cqi", transform: "rotate(-90deg)" },
    br: { bottom: "0.6cqi", right: "0.6cqi", transform: "rotate(180deg)" },
  };
  // ⚠️ IMPORTANT — this used to be an inline <svg> with `fill` attributes.
  // html2canvas-pro (used by exportCertificatePDF.js) drops SVG `fill`
  // attributes and falls back to BLACK, so downloaded / issued PDF
  // certificates were showing a black rectangle at each corner instead
  // of the gold L-brackets. Pure-CSS divs rasterise correctly.
  return (
    // 1.8cqi, not 4.6cqi. At 4.6cqi the bracket spanned 7.7px-59px from the
    // card edge while the two border rules sit at 27.8px and 35.7px, so the
    // arms crossed straight through both - the "overlapping corners" reported
    // repeatedly on issued certificates. At 1.8cqi the arm ends at ~24.8px,
    // clear of the outer rule, so ornament and border no longer intersect.
    // Stroke percentages are scaled up to keep the same drawn line weight in
    // the smaller box (5% of 51px == 13% of 20px).
    <div aria-hidden style={{ position: "absolute", width: "1.8cqi", height: "1.8cqi", ...map[position] }}>
      {/* Outer L — gold horizontal arm */}
      <div style={{
        position: "absolute", top: "5%", left: "15%",
        width: "70%", height: "13%",
        background: PALETTE.gold,
      }} />
      {/* Outer L — gold vertical arm */}
      <div style={{
        position: "absolute", top: "5%", left: "15%",
        width: "13%", height: "70%",
        background: PALETTE.gold,
      }} />
      {/* Inner L — softer gold, inset. */}
      <div style={{
        position: "absolute", top: "20%", left: "30%",
        width: "40%", height: "9.5%",
        background: PALETTE.goldSoft,
      }} />
      <div style={{
        position: "absolute", top: "20%", left: "30%",
        width: "9.5%", height: "45%",
        background: PALETTE.goldSoft,
      }} />
    </div>
  );
}

/* ============================================================
   Main certificate — landscape A4, self-scaling, self-fitting.
   ============================================================ */
export default function CertificateA4({
  staffName = "Jane Smith",
  staffRole = "",
  organisation = "",
  competencyTitle = "Introduction to Medical Cannabis Compliance",
  score = "\u2014",
  passMark = "80%",
  completedOn = "",
  validUntil = "",
  certId = "CC-2026-000000",
  version = "1.0",
  /**
   * Origin used to build the QR URL AND the URL shown under the QR.
   *
   * ⚠️ Defaults to the production domain `https://cannicomply.com`
   * — NOT to `window.location.origin`. Reason: the certificate is a
   * customer-facing artefact. Previewing, downloading, or verifying
   * from the preview environment must NEVER bake a preview URL into
   * the certificate. Callers that legitimately need a different
   * origin (e.g. internal QA) can pass one explicitly.
   */
  verifyOrigin = "https://cannicomply.com",
  verifyUrl,
  approvedBy = "CanniComply",
  approvedRole = "Compliance & Training Platform",
  className = "",
  style = {},
}) {
  const _verifyOriginTrim = String(verifyOrigin || "").replace(/\/+$/, "");
  const _qrUrl = `${_verifyOriginTrim}/verify/${encodeURIComponent(certId || "")}`;
  const _displayUrl =
    verifyUrl || _verifyOriginTrim.replace(/^https?:\/\//, "") + "/verify";

  // Sub-line uses en-dot separators. Empty parts collapse cleanly.
  const subLine = [staffRole, organisation].filter(Boolean).join(" \u00b7 ");

  return (
    <div
      className={className}
      style={{
        width: "100%",
        aspectRatio: "297 / 210",
        containerType: "inline-size",
        position: "relative",
        // Freeze iOS Safari's text-autosizing. Without this the cqi-based
        // sizes get inflated on iPhone because the cert reads as a "narrow
        // column of small text", blowing the layout out of its frame.
        WebkitTextSizeAdjust: "100%",
        textSizeAdjust: "100%",
        ...style,
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `
            radial-gradient(1000px 700px at 70% -10%, rgba(212,166,90,0.10), transparent 60%),
            radial-gradient(800px 600px at -10% 110%, rgba(15,93,63,0.06), transparent 60%),
            linear-gradient(180deg, ${PALETTE.paper} 0%, #F6F1DF 100%)
          `,
          border: `1px solid ${PALETTE.paperEdge}`,
          overflow: "hidden",
          fontFamily: "'Inter', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
          color: PALETTE.ink,
          fontSize: "1.5cqi",
          lineHeight: 1.4,
          boxShadow: "0 30px 60px -20px rgba(11,18,32,0.35), 0 2px 4px rgba(11,18,32,0.06)",
          WebkitTextSizeAdjust: "100%",
          textSizeAdjust: "100%",
        }}
      >
        {/* Watermark */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "'Playfair Display', Georgia, serif",
            fontStyle: "italic",
            fontSize: "18cqi",
            color: PALETTE.watermark,
            userSelect: "none",
            pointerEvents: "none",
            letterSpacing: "0.02em",
          }}
        >
          CanniComply
        </div>

        {/* Corner ornaments */}
        <CornerMarker position="tl" />
        <CornerMarker position="tr" />
        <CornerMarker position="bl" />
        <CornerMarker position="br" />

        {/* Double gold border */}
        {/* cqi, not %: a percentage inset resolves left/right against width but
            top/bottom against height, so on landscape A4 (1.41:1) these rules
            sat ~8px and ~10px closer to the top and bottom than to the sides.
            That put them off-centre against the corner ornaments, which are
            inset in cqi, and the bracket arms crossed them at uneven offsets -
            the "overlapping corners" in the exported PDF. cqi is width-relative
            on both axes, so all four sides now match. */}
        <div aria-hidden style={{ position: "absolute", inset: "2.4cqi", border: `1.5px solid ${PALETTE.gold}`, pointerEvents: "none" }} />
        <div aria-hidden style={{ position: "absolute", inset: "3.1cqi", border: `1px solid ${PALETTE.goldSoft}55`, pointerEvents: "none" }} />

        {/* Content — flex column pinned to the frame edges so the header
            stays at the top, the footer stays at the bottom, and the body /
            fleuron / metadata are spaced between them.
            Using justify-content: space-between prevents the body's 1fr
            growth from clipping the metadata band at the frame edge under
            html2canvas / print rendering. */}
        <div
          style={{
            position: "absolute",
            // top/bottom in cqi (width-relative) to match CornerMarker's own
            // cqi-based offsets -- % here would resolve top/bottom against the
            // card's *height* while the ornaments are width-relative on both
            // axes, which on this A4-landscape card let the content region
            // creep ~17px into the ornament's footprint.
            top: "6.4cqi",
            right: "5.2%",
            bottom: "6.4cqi",
            left: "5.2%",
            display: "flex",
            flexDirection: "column",
            gap: "1.4cqi",
          }}
        >
          {/* ─── HEADER ─────────────────────────────────────────
              Two-row composition designed for visual balance, not
              just mathematical centring:
              • Row 1 — a corner strip carrying the CanniComply logo
                on the left and a small "Verified certificate" tick
                on the right. Both are light-weight; neither
                dominates.
              • Row 2 — the certificate title stack (title, cert
                number) spans the full width and is centred against
                the page. Because nothing sits beside it, it reads
                as the unambiguous focal point.
              This yields the visual balance a customer's eye is
              looking for: quiet corners, a single dominant title.
              ─────────────────────────────────────────────────── */}
          <header
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1.6cqi",
            }}
          >
            {/* Corner strip */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                alignItems: "center",
                minHeight: "3.6cqi",
              }}
            >
              <div style={{ justifySelf: "start", width: "16cqi", maxWidth: 160 }}>
                <CannicomplyLogo variant="light" width={160} fluid />
              </div>
              <div style={{ justifySelf: "end" }}>
                <VerifiedBadge />
              </div>
            </div>

            {/* Title stack — full-width, mathematically centred */}
            <div
              style={{
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "0.55em",
              }}
            >
              <div
                style={{
                  textTransform: "uppercase",
                  letterSpacing: "0.44em",
                  fontSize: "2.35cqi",
                  color: PALETTE.gold,
                  fontFamily: "'Playfair Display', 'Times New Roman', Georgia, serif",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  lineHeight: 1,
                }}
              >
                Certificate of Competency
              </div>
              <div
                style={{
                  fontFamily: "'JetBrains Mono', 'SFMono-Regular', Menlo, monospace",
                  fontSize: "1.05cqi",
                  color: PALETTE.inkSoft,
                  letterSpacing: "0.18em",
                  lineHeight: 1,
                }}
              >
                No. {certId}
              </div>
              <div style={{ width: "44%", marginTop: "0.3cqi" }}>
                <Fleuron />
              </div>
            </div>
          </header>

          {/* ─── BODY — flex-grows between header and the metadata band ─── */}
          <main
            style={{
              flex: "1 1 auto",
              minHeight: 0,
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: "1.4cqi",
              padding: "0 4%",
            }}
          >
            <div
              style={{
                textTransform: "uppercase",
                letterSpacing: "0.42em",
                fontSize: "1.15cqi",
                color: PALETTE.inkMuted,
                fontWeight: 600,
              }}
            >
              This is to certify that
            </div>

            {/* Recipient name — auto-shrinks to fit long names */}
            <div style={{ padding: "0 4%" }}>
              <FitText
                as="h1"
                maxSize={68}
                minSize={22}
                maxLines={1}
                weight={600}
                style={{
                  fontFamily: "'Playfair Display', 'Times New Roman', Georgia, serif",
                  color: PALETTE.ink,
                  letterSpacing: "-0.005em",
                  margin: 0,
                  textAlign: "center",
                }}
              >
                {staffName}
              </FitText>
            </div>

            {subLine && (
              <div style={{ padding: "0 6%" }}>
                <FitText
                  maxSize={16}
                  minSize={7}
                  maxLines={1}
                  weight={500}
                  style={{
                    color: PALETTE.inkSoft,
                    letterSpacing: "0.06em",
                    textAlign: "center",
                  }}
                >
                  {subLine}
                </FitText>
              </div>
            )}

            <div
              style={{
                color: PALETTE.inkMuted,
                fontSize: "1.25cqi",
                letterSpacing: "0.02em",
                lineHeight: 1.5,
              }}
            >
              has successfully completed the required competency assessment in
            </div>

            {/* Competency title — italic serif, up to 2 lines */}
            <div style={{ padding: "0 6%" }}>
              <FitText
                maxSize={38}
                minSize={16}
                maxLines={2}
                weight={700}
                style={{
                  fontFamily: "'Playfair Display', 'Times New Roman', Georgia, serif",
                  fontStyle: "italic",
                  color: PALETTE.ink,
                  lineHeight: 1.15,
                  textAlign: "center",
                }}
              >
                {competencyTitle}
              </FitText>
            </div>
          </main>

          {/* ─── Ornamental divider ─── */}
          <Fleuron />

          {/* ─── METADATA BAND — 4 icon+label+value cells ─── */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
              gap: "1.8cqi",
              padding: "0.6cqi 0 1.4cqi 0",
            }}
          >
            <MetricCell Icon={IconBarChart} label="Score" value={score || "\u2014"} />
            <MetricCell Icon={IconRibbon}   label="Pass mark" value={passMark || "\u2014"} />
            <MetricCell Icon={IconCalendar} label="Completed" value={completedOn || "\u2014"} />
            <MetricCell Icon={IconClock}    label="Valid until" value={validUntil || "\u2014"} />
          </div>

          {/* ─── FOOTER — 1fr | auto | 1fr, mirrors the header ─── */}
          {/*
            Same balanced grid as the header. The signature block sits in
            the middle `auto` column and is therefore mathematically centred
            on the same page axis as the title. The Authorised panel is
            anchored left in its 1fr column; the Verify text + QR are
            anchored right in the mirrored 1fr column.

            The separator between the metadata band and the footer is a
            gold gradient hairline instead of a solid CSS border, so it
            reads as an ornament rather than as a hard 1px rule that
            html2canvas hardens into a visible bar in the exported PDF.
          */}
          <footer
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto 1fr",
              alignItems: "center",
              gap: "2.2cqi",
              paddingTop: "1.6cqi",
              position: "relative",
              fontSize: "1cqi",
            }}
          >
            <div
              aria-hidden
              style={{
                position: "absolute",
                top: 0,
                left: "6%",
                right: "6%",
                height: 1,
                background: `linear-gradient(to right, transparent, ${PALETTE.goldSoft}66 30%, ${PALETTE.goldSoft}66 70%, transparent)`,
                pointerEvents: "none",
              }}
            />
            {/* Authorised block — shield seal + text (left) */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "1.1cqi",
                padding: "1cqi 1.2cqi",
                border: `1px solid ${PALETTE.goldSoft}55`,
                background: "rgba(212,166,90,0.06)",
                minWidth: 0,
                justifySelf: "start",
                width: "100%",
                maxWidth: "28cqi",
              }}
            >
              <div
                aria-hidden
                style={{
                  width: "3.6cqi",
                  height: "3.6cqi",
                  minWidth: 28,
                  minHeight: 28,
                  borderRadius: "50%",
                  border: `1.4px solid ${PALETTE.goldSoft}`,
                  background: "rgba(212,166,90,0.10)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: "0 0 auto",
                }}
              >
                <IconShield />
              </div>
              <div style={{ minWidth: 0, flex: "1 1 auto", lineHeight: 1.3 }}>
                <div
                  style={{
                    fontSize: "1.15cqi",
                    fontWeight: 700,
                    color: PALETTE.ink,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  Authorised by {approvedBy}
                </div>
                <div style={{ color: PALETTE.inkSoft, fontSize: "0.95cqi", marginTop: "0.15em" }}>
                  {approvedRole}
                </div>
                <div
                  style={{
                    color: PALETTE.inkMuted,
                    fontSize: "0.82cqi",
                    marginTop: "0.1em",
                    fontStyle: "italic",
                  }}
                >
                  No physical signature required
                </div>
              </div>
            </div>

            {/* Signature block — perfectly centred to the page axis */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.35cqi",
                textAlign: "center",
                padding: "0 1cqi",
                minWidth: "22cqi",
              }}
            >
              {/*
                Signature wordmark — Playfair Display roman (NOT
                italic), upright, tight tracking. Italic weights of
                serif faces cause the eye to read a rightward slope
                even when the baseline is perfectly horizontal, so
                the italic treatment has been retired in favour of a
                level bold roman that reads as a formal wordmark.
              */}
              <div
                style={{
                  fontFamily: "'Playfair Display', 'Times New Roman', Georgia, serif",
                  fontStyle: "normal",
                  fontWeight: 700,
                  fontSize: "2.7cqi",
                  color: PALETTE.emerald,
                  letterSpacing: "-0.005em",
                  lineHeight: 1,
                }}
              >
                CanniComply
              </div>
              <div
                aria-hidden
                style={{
                  width: "70%",
                  height: 1,
                  background: `linear-gradient(to right, transparent, ${PALETTE.gold}, transparent)`,
                  marginTop: "0.25cqi",
                }}
              />
              <div
                style={{
                  textTransform: "uppercase",
                  letterSpacing: "0.28em",
                  fontSize: "0.82cqi",
                  color: PALETTE.ink,
                  fontWeight: 700,
                  marginTop: "0.3cqi",
                }}
              >
                CanniComply Compliance Team
              </div>
              <div
                style={{
                  textTransform: "uppercase",
                  letterSpacing: "0.22em",
                  fontSize: "0.72cqi",
                  color: PALETTE.inkMuted,
                  fontWeight: 600,
                }}
              >
                Quality
                <span style={{ color: PALETTE.goldSoft, margin: "0 0.4em" }}>·</span>
                Integrity
                <span style={{ color: PALETTE.goldSoft, margin: "0 0.4em" }}>·</span>
                Assurance
              </div>
            </div>

            {/* Verify text + QR — anchored right, mirrors the Authorised panel */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "1.1cqi",
                justifySelf: "end",
                width: "100%",
                maxWidth: "28cqi",
                minWidth: 0,
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.3cqi",
                  minWidth: 0,
                  flex: "1 1 auto",
                }}
              >
                <div
                  style={{
                    textTransform: "uppercase",
                    letterSpacing: "0.28em",
                    fontSize: "0.85cqi",
                    color: PALETTE.inkMuted,
                    fontWeight: 600,
                  }}
                >
                  Scan to verify
                </div>
                <FitText
                  maxSize={12}
                  minSize={7}
                  maxLines={1}
                  weight={600}
                  style={{
                    fontFamily: "'JetBrains Mono', 'SFMono-Regular', Menlo, monospace",
                    color: PALETTE.ink,
                    letterSpacing: "0.02em",
                  }}
                >
                  {_displayUrl}
                </FitText>
                <div
                  style={{
                    color: PALETTE.inkMuted,
                    fontSize: "0.82cqi",
                    fontStyle: "italic",
                    lineHeight: 1.35,
                  }}
                >
                  Certificate v{version}
                  <span style={{ color: PALETTE.goldSoft, margin: "0 0.35em" }}>·</span>
                  Issued digitally by CanniComply
                </div>
              </div>
              <QrBlock url={_qrUrl} size={78} />
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
}
